// Developer APIs Screen — Stripe-style 3-column API playground.
//
// Layout:
//   [Endpoint list 280px] | [Request panel flex-5] | [Response panel flex-7]
//
// State (kept here, threaded into the panels):
//   • selectedEndpointId — which endpoint is active
//   • paramValues — Record<endpointId, Record<paramName, string>>
//     (per-endpoint so switching endpoints doesn't clobber unrelated params)
//   • response — { status, elapsedMs, bytes, body } | null
//   • busy
//   • error message (network / API errors)
//
// Banner at the top makes the internal-preview posture explicit.

import React, {useMemo, useState} from 'react'
import clsx from 'clsx'
import {Loader2, PlugZap} from 'lucide-react'
import {SearchBar} from '../../components/common/SearchBar'
import {FormField} from '../../components/common/FormField'
import {CodeSnippet} from './CodeSnippet'
import {JsonViewer} from './JsonViewer'
import {
  APIError,
  ENDPOINTS,
  buildDisplayUrl,
  findEndpoint,
  type EndpointDef,
  type ResponseEnvelope,
} from './endpoints'

interface SuccessResponse {
  status: number
  elapsedMs: number
  bytes: number
  body: ResponseEnvelope
}

interface ErrorResponse {
  status: number
  elapsedMs: number
  bytes: number
  body: {error: {status: number; message: string; code?: string}}
  isError: true
}

type AnyResponse = SuccessResponse | ErrorResponse

export const DeveloperAPIsScreen: React.FC = () => {
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Per-endpoint param state so switching tabs preserves input.
  const [paramValuesByEndpoint, setParamValuesByEndpoint] = useState<
    Record<string, Record<string, string>>
  >({})
  const [response, setResponse] = useState<AnyResponse | null>(null)
  const [busy, setBusy] = useState(false)

  const selected = selectedId ? findEndpoint(selectedId) : null
  const currentParams = selectedId ? paramValuesByEndpoint[selectedId] ?? {} : {}

  const filteredEndpoints = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return ENDPOINTS
    return ENDPOINTS.filter(
      e =>
        e.name.toLowerCase().includes(q) ||
        e.path.toLowerCase().includes(q) ||
        e.group.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q),
    )
  }, [search])

  // Group endpoints by their `group` field for the left list section headers.
  const grouped = useMemo(() => {
    const map = new Map<string, EndpointDef[]>()
    for (const e of filteredEndpoints) {
      const arr = map.get(e.group) ?? []
      arr.push(e)
      map.set(e.group, arr)
    }
    return Array.from(map.entries())
  }, [filteredEndpoints])

  const setParam = (name: string, value: string) => {
    if (!selectedId) return
    setParamValuesByEndpoint(prev => ({
      ...prev,
      [selectedId]: {...(prev[selectedId] ?? {}), [name]: value},
    }))
  }

  const handleSelect = (id: string) => {
    setSelectedId(id)
    setResponse(null)
  }

  const handleRun = async () => {
    if (!selected) return
    setBusy(true)
    setResponse(null)
    const t0 = performance.now()
    try {
      const body = await selected.run(currentParams)
      const json = JSON.stringify(body)
      setResponse({
        status: 200,
        elapsedMs: Math.round(performance.now() - t0),
        bytes: new Blob([json]).size,
        body,
      })
    } catch (e) {
      const status = e instanceof APIError ? e.status : 500
      const message = e instanceof Error ? e.message : String(e)
      const code = e instanceof APIError ? e.code : undefined
      const errBody = {error: {status, message, code}}
      const json = JSON.stringify(errBody)
      setResponse({
        status,
        elapsedMs: Math.round(performance.now() - t0),
        bytes: new Blob([json]).size,
        body: errBody,
        isError: true,
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      {/* Preview banner — sits inside the screen, below the global Header. */}
      <div className="mx-xl mt-md rounded-md border border-statusYellow/40 bg-badgeYellowBg px-lg py-sm text-sm text-statusYellow">
        <span className="font-semibold">Internal preview · </span>
        anon key auth, scoped to the default mine. Endpoints, response shapes, and URLs are subject
        to change before public release.
      </div>

      {/* Three-column layout. min-h-0 lets the children scroll inside the
          parent's flex space without pushing the page off-screen. */}
      <div className="mx-xl my-md flex min-h-0 flex-1 gap-md">
        {/* ── Endpoint list ─────────────────────────────────────────── */}
        <aside className="flex w-[280px] shrink-0 flex-col rounded-lg border border-surfaceBorder bg-surfaceCard">
          <div className="border-b border-divider p-sm">
            <SearchBar value={search} onChange={setSearch} placeholder="Search endpoints..." />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto py-sm">
            {grouped.length === 0 ? (
              <div className="px-lg py-xxl text-center text-sm text-textMuted">
                No endpoints match "{search}".
              </div>
            ) : (
              grouped.map(([group, items]) => (
                <div key={group} className="mb-sm">
                  <div className="px-lg py-xs text-xs font-semibold uppercase tracking-wider text-textMuted">
                    {group}
                  </div>
                  {items.map(ep => (
                    <EndpointRow
                      key={ep.id}
                      endpoint={ep}
                      active={ep.id === selectedId}
                      onClick={() => handleSelect(ep.id)}
                    />
                  ))}
                </div>
              ))
            )}
          </div>
        </aside>

        {/* ── Request panel ─────────────────────────────────────────── */}
        <section className="flex min-w-0 flex-[5] flex-col rounded-lg border border-surfaceBorder bg-surfaceCard">
          {selected ? (
            <RequestPanel
              endpoint={selected}
              params={currentParams}
              setParam={setParam}
              onRun={handleRun}
              busy={busy}
            />
          ) : (
            <EmptyState
              title="Select an endpoint from the list to get started."
              icon={<PlugZap size={48} color="var(--color-textMuted)" />}
            />
          )}
        </section>

        {/* ── Response panel ────────────────────────────────────────── */}
        <section className="flex min-w-0 flex-[7] flex-col rounded-lg border border-surfaceBorder bg-surfaceCard">
          {!selected ? (
            <EmptyState
              title="Pick an endpoint to see request and response details."
              icon={<PlugZap size={48} color="var(--color-textMuted)" />}
            />
          ) : busy ? (
            <EmptyState
              title="Running request..."
              icon={<Loader2 size={48} className="animate-spin" color="var(--color-textMuted)" />}
            />
          ) : response == null ? (
            <EmptyState
              title="Click Run to see a live response."
              icon={<PlugZap size={48} color="var(--color-textMuted)" />}
            />
          ) : (
            <ResponseView response={response} />
          )}
        </section>
      </div>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────

const EndpointRow: React.FC<{
  endpoint: EndpointDef
  active: boolean
  onClick: () => void
}> = ({endpoint, active, onClick}) => (
  <button
    type="button"
    onClick={onClick}
    className={clsx(
      'flex w-full flex-col items-start gap-xs border-l-[3px] px-lg py-sm text-left transition-colors',
      active
        ? 'border-l-primary bg-sidebarActive'
        : 'border-l-transparent hover:bg-surfaceLight/40',
    )}
  >
    <div className="flex w-full items-center gap-sm">
      <MethodBadge method={endpoint.method} />
      <span
        className={clsx(
          'truncate text-md',
          active ? 'font-semibold text-textPrimary' : 'text-textPrimary',
        )}
      >
        {endpoint.name}
      </span>
    </div>
    <span className="line-clamp-2 text-xs text-textMuted">{endpoint.description}</span>
  </button>
)

const MethodBadge: React.FC<{method: 'GET'}> = ({method}) => (
  <span className="rounded-sm bg-badgeGreenBg px-sm py-[2px] font-mono text-[10px] font-bold uppercase tracking-wider text-statusGreen">
    {method}
  </span>
)

const RequestPanel: React.FC<{
  endpoint: EndpointDef
  params: Record<string, string>
  setParam: (name: string, value: string) => void
  onRun: () => void
  busy: boolean
}> = ({endpoint, params, setParam, onRun, busy}) => {
  const url = buildDisplayUrl(endpoint, params)
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-lg">
        <div className="flex flex-col gap-lg">
          {/* Title + description + use-case */}
          <div>
            <div className="flex items-center gap-md">
              <MethodBadge method={endpoint.method} />
              <h2 className="text-lg font-bold text-textPrimary">{endpoint.name}</h2>
            </div>
            <p className="mt-xs text-sm text-textSecondary">{endpoint.description}</p>
            <p className="mt-md text-xs italic text-textMuted">{endpoint.useCase}</p>
          </div>

          {/* URL preview strip */}
          <div className="rounded-md bg-surfaceLight px-lg py-sm">
            <div className="text-xs uppercase tracking-wider text-textMuted">Request URL</div>
            <code className="mt-xs block break-all font-mono text-sm text-textPrimary">
              <span className="text-textSecondary">GET </span>
              {url}
            </code>
          </div>

          {/* Parameters */}
          <div>
            <div className="mb-md text-xs font-semibold uppercase tracking-wider text-textMuted">
              Parameters
            </div>
            {endpoint.params.length === 0 ? (
              <div className="text-sm italic text-textMuted">
                This endpoint takes no parameters.
              </div>
            ) : (
              <div className="flex flex-col gap-md">
                {endpoint.params.map(p => (
                  <div key={p.name}>
                    <FormField
                      label={`${p.name}${p.required ? ' *' : ''}${
                        p.in === 'path' ? ' (path)' : ' (query)'
                      }`}
                      value={params[p.name] ?? ''}
                      onChange={v => setParam(p.name, v)}
                      placeholder={p.placeholder ?? p.default ?? p.type}
                    />
                    <div className="mt-xs text-xs text-textMuted">{p.description}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Code snippets */}
          <div>
            <div className="mb-md text-xs font-semibold uppercase tracking-wider text-textMuted">
              Code samples
            </div>
            <CodeSnippet endpoint={endpoint} params={params} />
          </div>
        </div>
      </div>

      {/* Run button — sticky bottom */}
      <div className="shrink-0 border-t border-divider bg-surface p-md">
        <button
          type="button"
          onClick={onRun}
          disabled={busy}
          className={clsx(
            'h-12 w-full rounded-md font-bold text-black transition-colors',
            'bg-primary hover:bg-primaryLight disabled:opacity-60',
            busy && 'animate-pulse',
          )}
        >
          {busy ? 'Running…' : 'Run Request'}
        </button>
      </div>
    </div>
  )
}

const ResponseView: React.FC<{response: AnyResponse}> = ({response}) => {
  const isError = 'isError' in response && response.isError
  return (
    <div className="flex min-h-0 flex-1 flex-col p-lg">
      {/* Metadata strip */}
      <div className="mb-md flex items-center gap-md rounded-md bg-surface px-lg py-sm">
        <StatusPill status={response.status} />
        <span className="text-xs text-textMuted">{response.elapsedMs} ms</span>
        <span className="text-xs text-textMuted">{formatBytes(response.bytes)}</span>
      </div>

      {/* Body */}
      {isError ? (
        <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-statusRed bg-surfaceCard p-lg">
          <h3 className="mb-md text-md font-semibold text-statusRed">Request failed</h3>
          <JsonViewer value={response.body} defaultCollapseDepth={3} />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-hidden">
          <JsonViewer value={response.body} defaultCollapseDepth={2} />
        </div>
      )}
    </div>
  )
}

const StatusPill: React.FC<{status: number}> = ({status}) => {
  const klass =
    status >= 200 && status < 300
      ? 'bg-badgeGreenBg text-statusGreen'
      : status >= 400 && status < 500
      ? 'bg-badgeYellowBg text-statusYellow'
      : 'bg-badgeRedBg text-statusRed'
  const label = status >= 200 && status < 300 ? 'OK' : status >= 400 && status < 500 ? 'Client error' : 'Server error'
  return (
    <span
      className={clsx(
        'rounded-round px-md py-xs text-xs font-semibold uppercase',
        klass,
      )}
    >
      {status} {label}
    </span>
  )
}

const EmptyState: React.FC<{title: string; icon: React.ReactNode}> = ({title, icon}) => (
  <div className="flex h-full flex-col items-center justify-center gap-md p-xxxl text-center">
    {icon}
    <div className="max-w-xs text-md text-textMuted">{title}</div>
  </div>
)

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}
